import { useTranslationWithPrefix } from 'commons/utils';
import {
  EmptyStateMexicoStatus,
  MessageCode,
  MessageSeverity,
  NextActionCode,
} from '../types';

interface StatusMessage {
  appearance: 'success' | 'primary' | 'neutral' | 'danger' | 'warning';
  colorText: 'success-textLow' | 'warning-textLow' | 'danger-textLow';
  title: string;
  description: string;
  isBtnRequired: boolean;
  primaryBtn?: string;
}

interface UseGetInstallationStatusAlertParams {
  status: EmptyStateMexicoStatus;
  requestDate?: string;
  messageCode?: MessageCode;
  messageSeverity?: MessageSeverity;
  nextActionCode?: NextActionCode;
  deadline?: string;
}

export const useGetInstallationStatusAlert = ({
  status,
  requestDate,
  messageCode,
  messageSeverity,
  nextActionCode,
  deadline,
}: UseGetInstallationStatusAlertParams): StatusMessage => {
  const { t } = useTranslationWithPrefix('landing.InstallationStatusAlert');

  // UnabledToUse means Pago Nube is installed but still disabled, so the
  // installation message ("activated") would contradict the landing button.
  if (messageCode && status !== EmptyStateMexicoStatus.UnabledToUse) {
    const appearance = getAppearanceFromSeverity(messageSeverity);
    const colorText = getColorTextFromSeverity(messageSeverity);

    return {
      appearance,
      colorText,
      title: t(`messages.${messageCode}.title`, { deadline }),
      description: t(`messages.${messageCode}.description`, { deadline }),
      isBtnRequired: !!nextActionCode,
      primaryBtn: nextActionCode ? t(`actions.${nextActionCode}`) : undefined,
    };
  }

  const statusMessages: Record<EmptyStateMexicoStatus, StatusMessage> = {
    [EmptyStateMexicoStatus.NotInstalled]: {
      appearance: 'warning',
      colorText: 'warning-textLow',
      title: t('notInstalled.title'),
      description: t('notInstalled.description'),
      isBtnRequired: false,
    },
    [EmptyStateMexicoStatus.Processing]: {
      appearance: 'warning',
      colorText: 'warning-textLow',
      title: t('processing.title'),
      description: t('processing.description', {
        requestDate,
        interpolation: { escapeValue: false },
      }),
      isBtnRequired: false,
    },
    [EmptyStateMexicoStatus.ActionRequiredMissingDocs]: {
      appearance: 'warning',
      colorText: 'warning-textLow',
      title: t('actionRequiredMissingDocs.title'),
      description: t('actionRequiredMissingDocs.description'),
      isBtnRequired: true,
      primaryBtn: t('actionRequiredMissingDocs.primaryBtn'),
    },
    [EmptyStateMexicoStatus.ActionRequiredMissingData]: {
      appearance: 'warning',
      colorText: 'warning-textLow',
      title: t('actionRequiredMissingData.title'),
      description: t('actionRequiredMissingData.description'),
      isBtnRequired: true,
      primaryBtn: t('actionRequiredMissingData.primaryBtn'),
    },
    [EmptyStateMexicoStatus.Success]: {
      appearance: 'success',
      colorText: 'success-textLow',
      title: t('success.title'),
      description: t('success.description'),
      isBtnRequired: false,
    },
    [EmptyStateMexicoStatus.UnabledToUse]: {
      appearance: 'success',
      colorText: 'success-textLow',
      title: t('unabledToUse.title'),
      description: t('unabledToUse.description'),
      isBtnRequired: !!nextActionCode,
      primaryBtn: nextActionCode ? t(`actions.${nextActionCode}`) : undefined,
    },
  };

  return statusMessages[status];
};

function getAppearanceFromSeverity(
  severity?: MessageSeverity,
): 'success' | 'primary' | 'neutral' | 'danger' | 'warning' {
  switch (severity) {
    case MessageSeverity.CRITICAL:
      return 'danger';
    case MessageSeverity.WARNING:
      return 'warning';
    case MessageSeverity.INFO:
    default:
      return 'success';
  }
}

function getColorTextFromSeverity(
  severity?: MessageSeverity,
): 'success-textLow' | 'warning-textLow' | 'danger-textLow' {
  switch (severity) {
    case MessageSeverity.CRITICAL:
      return 'danger-textLow';
    case MessageSeverity.WARNING:
      return 'warning-textLow';
    case MessageSeverity.INFO:
    default:
      return 'success-textLow';
  }
}
